// Presentación "NFTs y Blockchain para Cooperativas" — contenido informativo
// estático, solo visible para SuperAdmin (ver SuperAdminRoute en App.js).
// Origen: nft_cooperativas_presentacion.tsx (proyecto CoopNet). Las secciones
// 1,2,3,4,6,8,9 son marcadores de posición en el documento original.

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  TrendingUp,
  Code,
  Timeline,
  Speed,
  LocalAtm,
  Token
} from '@mui/icons-material';

const NFTCooperativasPresentation = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  const sections = [
    'Introducción',
    'Aplicaciones',
    'Inversión',
    'Casos Reales',
    'Hyperledger',
    'Implementación',
    'Legal',
    'Plan Acción',
    'Presupuesto',
    'Recursos'
  ];

  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, sections.length - 1));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));
  const handleStepClick = (step) => setActiveStep(step);

  // SECCIÓN 0: INTRODUCCIÓN
  const Introduccion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        ¿Qué son los NFTs?
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={3} sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Token sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="bold">Non-Fungible Tokens</Typography>
              <Typography variant="body1">Activos digitales únicos que representan propiedad en blockchain.</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">Características</Typography>
              <List>
                {['Unicidad', 'Verificable', 'Transferible', 'Programable'].map((item, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 5: IMPLEMENTACIÓN COMPLETA
  const Implementacion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Implementación Práctica con Hyperledger
      </Typography>

      <Grid container spacing={3}>
        {/* ARQUITECTURA */}
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Arquitectura Hyperledger Fabric
              </Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'white', p: 3, borderRadius: 2, fontFamily: 'monospace', fontSize: '0.85rem', overflowX: 'auto' }}>
                <pre style={{ margin: 0 }}>{`
┌─────────────────────────────────────────┐
│   APLICACIÓN WEB/MÓVIL COOPERATIVA      │
│  (Interfaz para miembros y admins)      │
└─────────────┬───────────────────────────┘
              │
    ┌─────────▼─────────┐
    │   APIs / SDK      │
    └─────────┬─────────┘
              │
┌─────────────▼──────────────────────────┐
│  HYPERLEDGER FABRIC NETWORK            │
│  • Peer Nodes (Miembros, Admin)        │
│  • Smart Contracts (Chaincode)         │
│  • Ordering Service                    │
│  • Certificate Authority               │
└─────────────────────────────────────────┘
                `}</pre>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* CRONOGRAMA DETALLADO */}
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Cronograma de Implementación
              </Typography>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tab label="Año 1" />
                <Tab label="Año 2" />
                <Tab label="Año 3+" />
              </Tabs>

              {tabValue === 0 && (
                <Grid container spacing={2}>
                  {[
                    { q: 'Q1', title: 'Educación y Planificación', items: ['Talleres stakeholders', 'Consultas legales', 'Diseño arquitectura'] },
                    { q: 'Q2', title: 'Desarrollo Piloto', items: ['Setup infraestructura', 'Smart contracts básicos', 'Interfaz simple'] },
                    { q: 'Q3', title: 'Testing Piloto', items: ['30 usuarios piloto', '1 propiedad tokenizada', 'Recolección feedback'] },
                    { q: 'Q4', title: 'Evaluación', items: ['Análisis resultados', 'GO/NO-GO', 'Planificación Año 2'] }
                  ].map((q, i) => (
                    <Grid size={{ xs: 12, md: 6 }} key={i}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50', height: '100%' }}>
                        <CardContent>
                          <Chip label={q.q} color="primary" sx={{ mb: 1 }} />
                          <Typography variant="h6" fontWeight="bold">{q.title}</Typography>
                          <List dense>
                            {q.items.map((item, j) => (
                              <ListItem key={j}>
                                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {tabValue === 1 && (
                <Grid container spacing={2}>
                  {[
                    { q: 'Q1', title: 'Desarrollo Completo', items: ['Infraestructura escalable', 'Todos smart contracts', 'App completa'] },
                    { q: 'Q2', title: 'Testing Riguroso', items: ['Auditorías seguridad', 'Testing carga', 'Beta 100 usuarios'] },
                    { q: 'Q3', title: 'Lanzamiento', items: ['Soft launch', 'General launch', 'Onboarding masivo'] },
                    { q: 'Q4', title: 'Estabilización', items: ['Optimizaciones', 'Nuevas features', 'Documentación'] }
                  ].map((q, i) => (
                    <Grid size={{ xs: 12, md: 6 }} key={i}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50', height: '100%' }}>
                        <CardContent>
                          <Chip label={q.q} color="secondary" sx={{ mb: 1 }} />
                          <Typography variant="h6" fontWeight="bold">{q.title}</Typography>
                          <List dense>
                            {q.items.map((item, j) => (
                              <ListItem key={j}>
                                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {tabValue === 2 && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50', p: 3 }}>
                  <Typography variant="h6" fontWeight="bold">Operación Continua</Typography>
                  <List>
                    {['Operación estable', 'Nuevas integraciones', 'Escalamiento', 'Intercooperación', 'Modelo replicable'].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* INFRAESTRUCTURA */}
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">Infraestructura Técnica</Typography>
              <Grid container spacing={2}>
                {[
                  { icon: <Code />, title: 'Hardware', specs: '4+ cores CPU, 8-16GB RAM, 50GB+ SSD' },
                  { icon: <Speed />, title: 'Rendimiento', specs: '3,500+ TPS, 99.5% Uptime, <2s Response' },
                  { icon: <LocalAtm />, title: 'Costos Cloud', specs: '$300-600/mes básico, $800-1,500/mes HA' }
                ].map((spec, i) => (
                  <Grid size={{ xs: 12, md: 4 }} key={i}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box sx={{ fontSize: 50, color: '#2196f3', mb: 1 }}>{spec.icon}</Box>
                      <Typography variant="h6" fontWeight="bold">{spec.title}</Typography>
                      <Typography variant="body2">{spec.specs}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* EQUIPO NECESARIO */}
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">Equipo Necesario</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Rol</strong></TableCell>
                      <TableCell><strong>Cantidad</strong></TableCell>
                      <TableCell><strong>Responsabilidades</strong></TableCell>
                      <TableCell align="right"><strong>Salario Anual</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Arquitecto Blockchain', '1', 'Diseño arquitectura, supervisión', '$80K-150K'],
                      ['Desarrolladores Blockchain', '2-3', 'Smart contracts, integración', '$60K-120K c/u'],
                      ['Desarrolladores Full-Stack', '2', 'Frontend, backend, APIs', '$50K-100K c/u'],
                      ['DevOps Engineer', '1', 'Infraestructura, deployment', '$60K-110K'],
                      ['QA/Testing', '1', 'Testing calidad, auditoría', '$40K-80K']
                    ].map((row, i) => (
                      <TableRow key={i}>
                        <TableCell><strong>{row[0]}</strong></TableCell>
                        <TableCell>{row[1]}</TableCell>
                        <TableCell>{row[2]}</TableCell>
                        <TableCell align="right"><Chip label={row[3]} size="small" color="primary" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* TIEMPO TOTAL */}
        <Grid size={12}>
          <Alert severity="info" icon={<Timeline />}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Tiempo Total: 6-12 meses</Typography>
            <List dense>
              {[
                'Setup inicial de red: 1-2 semanas',
                'Desarrollo smart contracts: 2-4 meses',
                'Integración sistemas: 2-3 meses',
                'Testing y auditoría: 1-2 meses'
              ].map((item, i) => (
                <ListItem key={i}><ListItemText primary={`• ${item}`} /></ListItem>
              ))}
            </List>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 7: PLAN DE ACCIÓN COMPLETO
  const PlanAccion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Plan de Acción para Cooperativas
      </Typography>

      <Grid container spacing={3}>
        {/* PASOS INMEDIATOS */}
        <Grid size={12}>
          <Card elevation={3} sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
                Pasos Inmediatos - Comenzar HOY
              </Typography>
              <Grid container spacing={2}>
                {[
                  { week: 'Semana 1', title: 'Educación Básica', tasks: ['Leer documentación', 'Ver videos blockchain', 'Compartir con junta', 'Agendar reunión'] },
                  { week: 'Semana 2-3', title: 'Exploración', tasks: ['Casos similares', 'Contactar cooperativas', 'Identificar consultores', 'Lista preguntas'] },
                  { week: 'Semana 4', title: 'Primera Consulta', tasks: ['Consultor blockchain', 'Abogado', 'Estimar costos', 'Evaluar viabilidad'] },
                  { week: 'Mes 2', title: 'Decisión', tasks: ['Presentar asamblea', 'Votación', 'Asignar presupuesto', 'Formar comité'] }
                ].map((phase, i) => (
                  <Grid size={{ xs: 12, md: 6 }} key={i}>
                    <Card variant="outlined">
                      <CardContent>
                        <Chip label={phase.week} color="success" sx={{ mb: 2 }} />
                        <Typography variant="h6" fontWeight="bold">{phase.title}</Typography>
                        <List dense>
                          {phase.tasks.map((task, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={task} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* CASOS DE USO PRIORIZADOS */}
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Casos de Uso Priorizados
              </Typography>
              <Grid container spacing={2}>
                {[
                  { p: '1', title: 'Tokenización Propiedades', color: '#f44336', roi: 'Alto', time: '6-12m', impact: ['Capital inmediato', 'Transacción -80%', 'Costos -50-70%'] },
                  { p: '2', title: 'Votación Digital', color: '#ff9800', roi: 'Medio', time: '3-6m', impact: ['Participación +40-60%', 'Costos -70%', 'Más rápido'] },
                  { p: '3', title: 'Gestión Membresías', color: '#ffc107', roi: 'Medio', time: '2-4m', impact: ['Admin simple', 'Sin fraude', 'Fácil transferir'] },
                  { p: '4', title: 'Auto Dividendos', color: '#4caf50', roi: 'Alto', time: '2-3m', impact: ['Ahorro 90%', 'Sin errores', 'Más frecuente'] }
                ].map((uc, i) => (
                  <Grid size={{ xs: 12, md: 6 }} key={i}>
                    <Card variant="outlined" sx={{ borderLeft: `5px solid ${uc.color}`, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Chip label={`Prioridad ${uc.p}`} sx={{ bgcolor: uc.color, color: 'white', mr: 2 }} />
                          <Typography variant="h6" fontWeight="bold">{uc.title}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Chip label={`ROI: ${uc.roi}`} size="small" />
                          <Chip label={uc.time} size="small" />
                        </Box>
                        <Typography variant="subtitle2" fontWeight="bold">Impacto:</Typography>
                        <List dense>
                          {uc.impact.map((item, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><TrendingUp color="success" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* KPIS */}
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Métricas de Éxito (KPIs)
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Categoría</strong></TableCell>
                      <TableCell><strong>Métrica</strong></TableCell>
                      <TableCell align="right"><strong>Objetivo</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Técnicos', 'Disponibilidad Sistema', '>99.5%'],
                      ['Técnicos', 'Tiempo Respuesta', '<2 seg'],
                      ['Técnicos', 'Transacciones/seg', '100+ TPS'],
                      ['Adopción', 'Tasa Adopción Mensual', '>5%'],
                      ['Adopción', 'Usuarios Activos', '>60%'],
                      ['Adopción', 'NPS', '>50'],
                      ['Financieros', 'Reducción Costos', '>30%'],
                      ['Financieros', 'ROI Proyecto', '18-24 meses'],
                      ['Gobernanza', 'Participación Votos', '>50%'],
                      ['Gobernanza', 'Tiempo Resolución', '-70%']
                    ].map((row, i) => (
                      <TableRow key={i}>
                        <TableCell><strong>{row[0]}</strong></TableCell>
                        <TableCell>{row[1]}</TableCell>
                        <TableCell align="right"><Chip label={row[2]} size="small" color="primary" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Se invocan como funciones (no como <Componente />) para que no se
  // remonten en cada cambio de estado, p. ej. al cambiar de pestaña.
  const renderSection = () => {
    switch (activeStep) {
      case 0: return Introduccion();
      case 5: return Implementacion();
      case 7: return PlanAccion();
      default: return <Typography variant="h4">Sección {activeStep + 1}: {sections[activeStep]}</Typography>;
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            NFTs y Blockchain para Cooperativas
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {sections.map((label, index) => (
              <Step key={label}>
                <StepLabel sx={{ cursor: 'pointer' }} onClick={() => handleStepClick(index)}>
                  <Typography variant="caption">{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Box sx={{ mb: 4 }}>{renderSection()}</Box>

        <Paper elevation={3} sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0}>Anterior</Button>
          <Typography variant="body2">Sección {activeStep + 1} de {sections.length}</Typography>
          <Button variant="contained" onClick={handleNext} disabled={activeStep === sections.length - 1}>Siguiente</Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default NFTCooperativasPresentation;
